import { useEffect, useRef } from 'react'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { storageKey } from '@/constant/storage'
import { appWebViewUserSyncStatusState, userState } from 'Store/user'
import { homeNSFWState, DEFAULT_NSFW } from 'Store/home'
import { User } from 'Types/user'
import { getUser } from 'Apis/user'
import { transformImage } from 'Utils/img'
import { useGuideStatusActions } from '@/store/guideStatus'
import { getAuthToken, hasJwtNotExpired } from '@/lib/auth/jwt'
import {
  isRunningInAppWebView,
  shouldRefreshUserFromInfo,
} from '@/utils/appWebView'

export const useInit = () => {
  const [user, setUser] = useRecoilState(userState)
  const setAppWebViewUserSyncStatus = useSetRecoilState(
    appWebViewUserSyncStatusState
  )
  const setNsfw = useSetRecoilState(homeNSFWState)
  const { initializeForUser } = useGuideStatusActions()
  const initializedUserIdRef = useRef<string | null>(null)
  const userRefreshAttemptedRef = useRef(false)

  useEffect(() => {
    const updateUser = async () => {
      try {
        const newUser = await getUser({ skipLoginModalOn401: true })
        if (newUser) {
          const localUser: User = {
            ...newUser,
            avatar_url: transformImage(newUser.avatar_url, undefined, 90),
          }
          setUser(localUser)
          localStorage.setItem(storageKey.USER, JSON.stringify(localUser))
          return true
        }
      } catch (error) {
        return false
      }
      return false
    }
    let lastUser: User | undefined
    if (typeof window !== 'undefined') {
      const userJson = localStorage?.getItem(storageKey.USER)
      if (userJson) {
        try {
          lastUser = JSON.parse(userJson)
        } catch (e) {
          console.warn('Failed to parse user:', e)
        }
        if (lastUser) {
          // 初始假设用户已填写基础规则
          lastUser.basic_rules_completed = true
        }
      }
    }
    const appWebView = isRunningInAppWebView()

    if (appWebView && !userRefreshAttemptedRef.current) {
      // Native may overwrite /user with a partial snapshot whenever a new
      // WebView opens. Always give /user/info one chance to restore the
      // complete profile, even when userState is already hydrated.
      const token = getAuthToken()
      const hasValidToken = !!token && hasJwtNotExpired(token)
      const shouldRefresh = shouldRefreshUserFromInfo({
        isAppWebView: true,
        hasUser: !!user,
        hasStoredUser: !!lastUser,
        hasValidToken,
        refreshAttempted: userRefreshAttemptedRef.current,
      })
      userRefreshAttemptedRef.current = true

      if (!user && lastUser) {
        setUser(lastUser)
      }

      if (!shouldRefresh) {
        setAppWebViewUserSyncStatus('ready')
        return
      }

      setAppWebViewUserSyncStatus('loading')
      void updateUser().finally(() => {
        setAppWebViewUserSyncStatus('ready')
      })
      return
    }

    if (appWebView) return
    if (user || userRefreshAttemptedRef.current) return

    const shouldCheckToken = !lastUser
    const token = shouldCheckToken ? getAuthToken() : null
    const hasValidToken = !!token && hasJwtNotExpired(token)
    if (
      !shouldRefreshUserFromInfo({
        isAppWebView: false,
        hasUser: !!user,
        hasStoredUser: !!lastUser,
        hasValidToken,
        refreshAttempted: userRefreshAttemptedRef.current,
      })
    ) {
      return
    }

    if (lastUser) {
      setUser(lastUser)
    }

    userRefreshAttemptedRef.current = true
    void updateUser()
  }, [setAppWebViewUserSyncStatus, setUser, user])

  useEffect(() => {
    // NSFW（Limitless）开关以后端 user.nsfw 为权威源。
    // homeNSFWState 仅写入 localStorage、不回读，刷新后会回落到默认值，
    // 因此每当 user（含从 localStorage 水合或后端刷新）就位时按权威值重新播种，
    // 避免出现「删了 homeNSFWState 缓存或刷新后开关被关」的问题。
    // 登出后 user 置空（setUser(null) 是纯前端重置、不刷新页面），此时复位为
    // DEFAULT_NSFW，否则内存里会残留上一用户的开启值，直到刷新才回落。
    // ⚠️ 这是 homeNSFWState 的唯一同步入口，atom 定义在 store/home.ts，删除此段会让该 bug 复发。
    if (!user?.user_id) {
      setNsfw(DEFAULT_NSFW)
      return
    }
    setNsfw(!!user.nsfw)
  }, [setNsfw, user?.user_id, user?.nsfw])

  useEffect(() => {
    const userId = user?.user_id || null
    if (!userId) {
      initializedUserIdRef.current = null
      return
    }

    if (initializedUserIdRef.current === userId) return
    initializedUserIdRef.current = userId

    initializeForUser(userId).catch((error) => {
      console.error('initialize guide status failed:', error)
    })
  }, [initializeForUser, user?.user_id])

  return null
}
